/**
 * MeshOps — Static utility class for mesh manipulation.
 *
 * Provides CSG boolean operations, geometry transforms, subdivision, and decimation.
 */

import * as THREE from 'three/webgpu';
import { Brush, Evaluator, ADDITION, SUBTRACTION, INTERSECTION } from 'three-bvh-csg';
import { mergeGeometries, mergeVertices, toCreasedNormals, computeMikkTSpaceTangents } from 'three/addons/utils/BufferGeometryUtils.js';
import * as MikkTSpace from 'three/addons/libs/mikktspace.module.js';
import { SimplifyModifier } from 'three/addons/modifiers/SimplifyModifier.js';
import { Logger } from '../core/Logger.js';

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function csgOp(a: THREE.Mesh, b: THREE.Mesh, op: number): THREE.Mesh {
  const brushA = new Brush(a.geometry, a.material as THREE.Material);
  brushA.applyMatrix4(a.matrixWorld);
  brushA.updateMatrixWorld(true);

  const brushB = new Brush(b.geometry, b.material as THREE.Material);
  brushB.applyMatrix4(b.matrixWorld);
  brushB.updateMatrixWorld(true);

  const evaluator = new Evaluator();
  const result = evaluator.evaluate(brushA, brushB, op as any);
  return new THREE.Mesh(result.geometry, a.material);
}

// ---------------------------------------------------------------------------
// MeshOps
// ---------------------------------------------------------------------------

/** Options for {@link MeshOps.smoothModel}. */
export interface SmoothOptions {
  /** Crease angle in degrees (45–60 typical). Default 50. */
  creaseAngle?: number;
  /** Weld near-duplicate vertices before creasing. Default false. */
  weld?: boolean;
  /** Weld tolerance when weld=true. Default 1e-4. */
  weldTolerance?: number;
  /** Generate MikkTSpace tangents (needed by tangent-space detail normals). Default false. */
  tangents?: boolean;
  /** Skip alpha-tested / transparent / transmissive materials (foliage guard). Default true. */
  skipAlpha?: boolean;
}

/** Result of {@link MeshOps.smoothModel}. */
export interface SmoothStats { meshes: number; skipped: number; }

export class MeshOps {

  // -----------------------------------------------------------------------
  // CSG Boolean Operations
  // -----------------------------------------------------------------------

  /** Compute the boolean union of two meshes. */
  static union(a: THREE.Mesh, b: THREE.Mesh): THREE.Mesh {
    return csgOp(a, b, ADDITION as unknown as number);
  }

  /** Compute the boolean subtraction (a minus b). */
  static subtract(a: THREE.Mesh, b: THREE.Mesh): THREE.Mesh {
    return csgOp(a, b, SUBTRACTION as unknown as number);
  }

  /** Compute the boolean intersection of two meshes. */
  static intersect(a: THREE.Mesh, b: THREE.Mesh): THREE.Mesh {
    return csgOp(a, b, INTERSECTION as unknown as number);
  }

  // -----------------------------------------------------------------------
  // Geometry Transforms
  // -----------------------------------------------------------------------

  /**
   * Merge multiple meshes into a single mesh.
   * Each mesh's world transform is baked into its geometry clone before merging.
   */
  static merge(meshes: THREE.Mesh[]): THREE.Mesh {
    const geometries = meshes.map((m) => {
      const g = m.geometry.clone();
      m.updateMatrixWorld(true);
      g.applyMatrix4(m.matrixWorld);
      return g;
    });
    const merged = mergeGeometries(geometries);
    // Dispose the clones now that they've been merged — otherwise every call
    // leaks N BufferGeometry allocations on the GPU.
    for (const g of geometries) g.dispose();
    if (!merged) throw new Error('MeshOps.merge: mergeGeometries returned null');
    return new THREE.Mesh(merged, meshes[0]?.material);
  }

  /**
   * Split a mesh by a plane into two halves (front / back).
   * Triangles are classified by their centroid; no clipping is performed.
   */
  static split(mesh: THREE.Mesh, plane: THREE.Plane): [THREE.Mesh, THREE.Mesh] {
    const geo = mesh.geometry;
    const posAttr = geo.getAttribute('position') as THREE.BufferAttribute;
    const index = geo.index;
    const triCount = index ? index.count / 3 : posAttr.count / 3;

    // Collect all attribute metadata. Use the generic BufferAttribute type
    // (which getAttribute returns) rather than the concrete one — GLTFLoader
    // produces InterleavedBufferAttribute for non-default byteStride buffers,
    // and those must be read through getComponent (see copy loop below).
    const attrNames = Object.keys(geo.attributes);
    const attrs = attrNames.map(name => ({
      name,
      attr: geo.getAttribute(name),
    }));

    // Per-side buffers for every attribute
    const frontBufs: Record<string, number[]> = {};
    const backBufs: Record<string, number[]> = {};
    for (const name of attrNames) {
      frontBufs[name] = [];
      backBufs[name] = [];
    }

    const centroid = new THREE.Vector3();

    for (let t = 0; t < triCount; t++) {
      const i0 = index ? index.getX(t * 3) : t * 3;
      const i1 = index ? index.getX(t * 3 + 1) : t * 3 + 1;
      const i2 = index ? index.getX(t * 3 + 2) : t * 3 + 2;

      // Compute centroid from position attribute
      centroid.set(
        (posAttr.getX(i0) + posAttr.getX(i1) + posAttr.getX(i2)) / 3,
        (posAttr.getY(i0) + posAttr.getY(i1) + posAttr.getY(i2)) / 3,
        (posAttr.getZ(i0) + posAttr.getZ(i1) + posAttr.getZ(i2)) / 3,
      );

      const side = plane.distanceToPoint(centroid) >= 0;

      for (const idx of [i0, i1, i2]) {
        for (const { name, attr } of attrs) {
          const buf = side ? frontBufs[name] : backBufs[name];
          // Read via getComponent — for an InterleavedBufferAttribute `.array`
          // is the shared interleaved buffer and the per-element stride/offset
          // (not itemSize) govern the layout, so raw `array[idx*itemSize+c]`
          // would copy garbage. getComponent handles both attribute kinds.
          for (let c = 0; c < attr.itemSize; c++) {
            buf.push(attr.getComponent(idx, c));
          }
        }
      }
    }

    function buildMesh(bufs: Record<string, number[]>): THREE.Mesh {
      const g = new THREE.BufferGeometry();
      for (const { name, attr } of attrs) {
        const data = bufs[name];
        g.setAttribute(name, new THREE.Float32BufferAttribute(data, attr.itemSize));
      }
      return new THREE.Mesh(g, mesh.material);
    }

    return [buildMesh(frontBufs), buildMesh(backBufs)];
  }

  /** Flip all normals and reverse winding order. */
  static flipNormals(geometry: THREE.BufferGeometry): void {
    const normalAttr = geometry.getAttribute('normal');
    if (normalAttr) {
      // Negate per component via accessors. For an InterleavedBufferAttribute
      // `.array` is the shared buffer (positions/uvs/normals together), so a
      // raw `arr[i] = -arr[i]` sweep would corrupt every other attribute.
      for (let i = 0; i < normalAttr.count; i++) {
        for (let c = 0; c < normalAttr.itemSize; c++) {
          normalAttr.setComponent(i, c, -normalAttr.getComponent(i, c));
        }
      }
      normalAttr.needsUpdate = true;
    }

    // Reverse winding order in index buffer
    const index = geometry.index;
    if (index) {
      const idxArr = index.array;
      for (let i = 0; i < idxArr.length; i += 3) {
        const tmp = idxArr[i + 1];
        idxArr[i + 1] = idxArr[i + 2];
        idxArr[i + 2] = tmp;
      }
      index.needsUpdate = true;
    } else {
      // Non-indexed: swap vertices 1 & 2 of each triangle across ALL attributes
      // (position, normal, uv, tangent, color, etc.). Use getComponent/
      // setComponent so InterleavedBufferAttributes (shared buffer keyed by
      // stride/offset, not itemSize) are read/written correctly — raw `.array`
      // indexing by itemSize would scramble the interleaved layout in place.
      for (const name of Object.keys(geometry.attributes)) {
        const attr = geometry.getAttribute(name);
        const itemSize = attr.itemSize;
        const vertCount = attr.count;

        for (let v = 0; v < vertCount; v += 3) {
          // Swap vertex 1 (v+1) and vertex 2 (v+2) of each triangle
          for (let c = 0; c < itemSize; c++) {
            const tmp = attr.getComponent(v + 1, c);
            attr.setComponent(v + 1, c, attr.getComponent(v + 2, c));
            attr.setComponent(v + 2, c, tmp);
          }
        }
        attr.needsUpdate = true;
      }
    }
  }

  /** Recompute smooth vertex normals from face geometry. */
  static recomputeNormals(geometry: THREE.BufferGeometry): void {
    geometry.computeVertexNormals();
  }

  /** Recompute tangent vectors (requires normals and UVs). */
  static recomputeTangents(geometry: THREE.BufferGeometry): void {
    geometry.computeTangents();
  }

  /**
   * Recompute shading normals with an angle-based crease: faces meeting BELOW
   * `angleDeg` are smoothed (shared normals), at/above it stay sharp. The primary
   * fix for faceted GLB shading — it rewrites ONLY the normal attribute (vertex
   * positions never move, so silhouette/colliders/UVs are unchanged) and returns
   * a NEW, de-indexed geometry. 45–60° suits hard-surface/architectural meshes.
   */
  static creaseNormals(geometry: THREE.BufferGeometry, angleDeg = 50): THREE.BufferGeometry {
    return toCreasedNormals(geometry, (angleDeg * Math.PI) / 180);
  }

  /**
   * Generate high-quality MikkTSpace tangents (the glTF-standard convention,
   * superseding the lower-quality built-in computeTangents). Requires position,
   * normal AND uv; de-indexes the geometry. Async — the wasm module must finish
   * loading first. No-op (returns the input) when uv or normal are absent, so a
   * tangent-less mesh never throws.
   */
  static async mikkTangents(geometry: THREE.BufferGeometry): Promise<THREE.BufferGeometry> {
    if (!geometry.getAttribute('uv') || !geometry.getAttribute('normal')) return geometry;
    await MikkTSpace.ready; // wasm must be initialized before computeMikkTSpaceTangents
    return computeMikkTSpaceTangents(geometry, MikkTSpace);
  }

  /**
   * Center the geometry around the origin and adjust the mesh position accordingly.
   * After this call the mesh's world-space position is unchanged but the local pivot
   * sits at the geometry's bounding-box center.
   */
  static centerPivot(mesh: THREE.Mesh): void {
    const geo = mesh.geometry;
    geo.computeBoundingBox();
    const center = new THREE.Vector3();
    geo.boundingBox!.getCenter(center);

    // Shift geometry so its pivot is at the bounding-box center
    geo.translate(-center.x, -center.y, -center.z);

    // The center offset is in local space. To map it to world space
    // we must SCALE first, then ROTATE (the standard TRS order for
    // local→world). Rotating before scaling with a non-uniform scale
    // produces the wrong world offset and visually jumps the mesh.
    const worldCenter = center.clone().multiply(mesh.scale).applyQuaternion(mesh.quaternion);
    mesh.position.add(worldCenter);
    mesh.updateMatrixWorld(true);
  }

  /** Weld (merge) vertices within the given tolerance. */
  static weldVertices(geometry: THREE.BufferGeometry, tolerance = 1e-4): THREE.BufferGeometry {
    return mergeVertices(geometry, tolerance);
  }

  /**
   * Traverse an Object3D and smooth every eligible Mesh's geometry IN PLACE
   * (replaces `mesh.geometry` with the processed result and disposes the old).
   * Skips meshes with `userData.noSmooth === true` and — when `skipAlpha` — any
   * alpha-tested / transparent / transmissive material (the foliage backstop).
   * One bad mesh is logged and skipped, never aborting the whole model.
   *
   * Precondition: call on a freshly-loaded master BEFORE instancing/cloning — it
   * disposes replaced geometry, which would break already-existing clones that
   * share it by reference.
   */
  static async smoothModel(root: THREE.Object3D, opts: SmoothOptions = {}): Promise<SmoothStats> {
    const { creaseAngle = 50, weld = false, weldTolerance = 1e-4, tangents = false, skipAlpha = true } = opts;
    const targets: THREE.Mesh[] = [];
    root.traverse((o) => { if ((o as THREE.Mesh).isMesh) targets.push(o as THREE.Mesh); });

    let meshes = 0;
    let skipped = 0;
    for (const mesh of targets) {
      const noSmooth = (mesh.userData as { noSmooth?: boolean })?.noSmooth === true;
      if (noSmooth || (skipAlpha && MeshOps.isAlphaMaterial(mesh.material))) { skipped++; continue; }
      try {
        let geo = mesh.geometry;
        if (weld) geo = mergeVertices(geo, weldTolerance);
        let next = MeshOps.creaseNormals(geo, creaseAngle);
        if (tangents) next = await MeshOps.mikkTangents(next);
        if (next !== mesh.geometry) {
          const old = mesh.geometry;
          mesh.geometry = next;
          old.dispose();
        }
        meshes++;
      } catch (e) {
        skipped++;
        Logger.warn('MeshOps', `smoothModel: skipped a mesh — ${(e as Error).message}`);
      }
    }
    return { meshes, skipped };
  }

  /** True if any material in the slot is alpha-tested, transparent, or transmissive. */
  private static isAlphaMaterial(material: THREE.Material | THREE.Material[]): boolean {
    const mats = Array.isArray(material) ? material : [material];
    return mats.some((m) => {
      const mm = m as unknown as { alphaTest?: number; transparent?: boolean; transmission?: number };
      return (mm.alphaTest ?? 0) > 0 || mm.transparent === true || (mm.transmission ?? 0) > 0;
    });
  }

  // -----------------------------------------------------------------------
  // Subdivision & Decimation
  // -----------------------------------------------------------------------

  /**
   * Midpoint subdivision: each triangle is split into 4 by inserting edge midpoints.
   * Geometry is first welded to ensure indexed form.
   */
  static subdivide(geometry: THREE.BufferGeometry, iterations = 1): THREE.BufferGeometry {
    let geo = mergeVertices(geometry.clone());

    for (let iter = 0; iter < iterations; iter++) {
      const index = geo.index;

      if (!index) {
        throw new Error('MeshOps.subdivide: expected indexed geometry after mergeVertices');
      }

      // Collect all attributes generically
      const attrNames = Object.keys(geo.attributes);
      const attrMeta = attrNames.map(name => {
        const attr = geo.getAttribute(name) as THREE.BufferAttribute;
        return { name, itemSize: attr.itemSize, oldArray: attr.array as Float32Array };
      });

      const vertexCount = (geo.getAttribute('position') as THREE.BufferAttribute).count;
      const idxArr = index.array;
      const triCount = idxArr.length / 3;

      // Per-attribute expanding buffers — start with copies of existing data
      const newData: Record<string, number[]> = {};
      for (const { name, oldArray } of attrMeta) {
        newData[name] = Array.from(oldArray);
      }

      let nextVertexIndex = vertexCount;
      const edgeMidpoints = new Map<string, number>();

      function edgeKey(a: number, b: number): string {
        return a < b ? `${a}_${b}` : `${b}_${a}`;
      }

      function getMidpoint(a: number, b: number): number {
        const key = edgeKey(a, b);
        const existing = edgeMidpoints.get(key);
        if (existing !== undefined) return existing;

        const midIdx = nextVertexIndex++;
        // Interpolate ALL attributes at the midpoint
        for (const { name, itemSize, oldArray } of attrMeta) {
          const buf = newData[name];
          for (let c = 0; c < itemSize; c++) {
            buf.push((oldArray[a * itemSize + c] + oldArray[b * itemSize + c]) / 2);
          }
        }
        edgeMidpoints.set(key, midIdx);
        return midIdx;
      }

      const newIndices: number[] = [];

      for (let t = 0; t < triCount; t++) {
        const a = idxArr[t * 3];
        const b = idxArr[t * 3 + 1];
        const c = idxArr[t * 3 + 2];

        const ab = getMidpoint(a, b);
        const bc = getMidpoint(b, c);
        const ca = getMidpoint(c, a);

        // 4 sub-triangles
        newIndices.push(a, ab, ca);
        newIndices.push(ab, b, bc);
        newIndices.push(ca, bc, c);
        newIndices.push(ab, bc, ca);
      }

      const newGeo = new THREE.BufferGeometry();
      for (const { name, itemSize } of attrMeta) {
        newGeo.setAttribute(
          name,
          new THREE.Float32BufferAttribute(new Float32Array(newData[name]), itemSize),
        );
      }
      newGeo.setIndex(newIndices);
      // Recompute normals only if original had them (midpoint-averaged normals aren't unit-length)
      if (geo.getAttribute('normal')) {
        newGeo.computeVertexNormals();
      }

      geo = newGeo;
    }

    return geo;
  }

  /**
   * Decimate (simplify) geometry to approximately `targetRatio` of the original vertex count.
   * @param targetRatio — fraction in (0, 1). E.g. 0.5 keeps ~50 % of vertices.
   */
  static decimate(geometry: THREE.BufferGeometry, targetRatio: number): THREE.BufferGeometry {
    if (targetRatio <= 0 || targetRatio >= 1) {
      throw new RangeError('MeshOps.decimate: targetRatio must be in (0, 1)');
    }

    const modifier = new SimplifyModifier();
    const vertexCount = geometry.getAttribute('position').count;
    const removeCount = Math.floor(vertexCount * (1 - targetRatio));
    return modifier.modify(geometry, removeCount);
  }
}
